import {
  PhoneIcon,
  GlobeIcon,
  FileTextIcon,
  SparkleIcon,
  QuotesIcon,
  TrophyIcon,
  SlackLogoIcon,
  GoogleDriveLogoIcon,
  HeadsetIcon,
  type Icon,
} from '@phosphor-icons/react';
import { format, parseISO, isValid } from 'date-fns';

import type {
  CitationCardData,
  CitationSourceType,
} from './CitationCard.types';
import type { CitationSource } from '../../types';

/** Generic entity resolver callback — modules provide their own implementation (e.g. entity graph). */
export type EntityNodeResolver = (
  nodeId: string,
) => { url?: string | null; name?: string } | undefined;

const SOURCE_TYPE_ICONS: Record<CitationSourceType, Icon> = {
  EvidenceQuote: QuotesIcon,
  GongCallTranscript: HeadsetIcon,
  GoogleDriveFile: GoogleDriveLogoIcon,
  NormalizedCallTranscript: PhoneIcon,
  NormalizedCallTranscriptV2: PhoneIcon,
  WinLossInterviewReport: TrophyIcon,
  WinLossInterviewReportV2: TrophyIcon,
  WinLossTranscriptFull: TrophyIcon,
  WinLossTranscriptFullV2: TrophyIcon,
  StakCard: SparkleIcon,
  MarkdownSource: FileTextIcon,
  MarkdownSourceV2: FileTextIcon,
  SlackMessage: SlackLogoIcon,
  Message: FileTextIcon,
  web: GlobeIcon,
  unknown: FileTextIcon,
};

export function getCitationIcon(sourceType?: CitationSourceType): Icon {
  if (!sourceType) return FileTextIcon;
  return SOURCE_TYPE_ICONS[sourceType] ?? FileTextIcon;
}

export function getExternalLinkLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function getDomainFromUrl(url: string): string | undefined {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return undefined;
  }
}

const VALID_SOURCE_TYPES: ReadonlySet<string> = new Set<CitationSourceType>([
  'EvidenceQuote',
  'GongCallTranscript',
  'GoogleDriveFile',
  'NormalizedCallTranscript',
  'NormalizedCallTranscriptV2',
  'WinLossInterviewReport',
  'WinLossInterviewReportV2',
  'WinLossTranscriptFull',
  'WinLossTranscriptFullV2',
  'StakCard',
  'MarkdownSource',
  'MarkdownSourceV2',
  'SlackMessage',
  'Message',
  'web',
  'unknown',
]);

function isValidSourceType(value: string): value is CitationSourceType {
  return VALID_SOURCE_TYPES.has(value);
}

const INDEXER_PATH_TO_SOURCE_TYPE: Record<string, CitationSourceType> = {
  'curated-snippets': 'StakCard',
  'evidence-quotes': 'EvidenceQuote',
  'markdown-source': 'MarkdownSource',
  'stak-cards': 'StakCard',
  'win-loss-interview-report-v2': 'WinLossInterviewReportV2',
  'win-loss-transcript-full-v2': 'WinLossTranscriptFullV2',
  'normalized-call-transcripts-v2': 'NormalizedCallTranscriptV2',
};

const INDEXER_CITATION_PATH_RE =
  /\/(curated-snippets|evidence-quotes|markdown-source|stak-cards|win-loss-interview-report-v2|win-loss-transcript-full-v2|normalized-call-transcripts-v2)\/[\w-]+(?:\/\d+)?\/citation\/$/;

/** Fallback: derives source type from indexer URL path when `source_name` is absent. */
export function deriveSourceTypeFromUrl(
  url: string,
): CitationSourceType | undefined {
  const match = INDEXER_CITATION_PATH_RE.exec(url);
  if (!match?.[1]) return undefined;
  return INDEXER_PATH_TO_SOURCE_TYPE[match[1]];
}

function formatDisplayDate(
  isoDate: string | undefined | null,
): string | undefined {
  if (!isoDate) return undefined;
  const parsed = parseISO(isoDate);
  if (!isValid(parsed)) return undefined;
  return format(parsed, 'MMM d, yyyy');
}

type ResolvedEntity = {
  url: string | null;
  name: string | undefined;
};

export function resolveEntity(
  orgUnitId: string | undefined,
  getNode: EntityNodeResolver | undefined,
): ResolvedEntity {
  if (!orgUnitId || !getNode) return { url: null, name: undefined };
  const node = getNode(orgUnitId);
  return { url: node?.url ?? null, name: node?.name };
}

/** Resolves source type from `source_name`, falling back to URL-based derivation for legacy data. */
export function resolveSourceType(
  sourceName: string | undefined | null,
  url: string,
): CitationSourceType {
  if (sourceName && isValidSourceType(sourceName)) return sourceName;
  return deriveSourceTypeFromUrl(url) ?? 'unknown';
}

/** Handles both new (source_name, significant_date, org_units) and legacy (source_type, display_date, org_unit_id) payloads. */
export function mapCitationSourceToCardData(
  source: CitationSource,
  options?: {
    getNode?: EntityNodeResolver;
  },
): CitationCardData {
  const { citation_data } = source;

  const resolvedSourceType = resolveSourceType(
    citation_data.source_name ?? citation_data.source_type,
    source.url,
  );

  const displayDate =
    formatDisplayDate(citation_data.significant_date) ??
    citation_data.display_date ??
    undefined;

  const orgUnitId =
    citation_data.org_units?.[0]?.id ?? citation_data.org_unit_id ?? undefined;

  const entity = resolveEntity(orgUnitId, options?.getNode);

  return {
    title: citation_data.title || 'Unknown source',
    description: citation_data.description ?? undefined,
    displayDate,
    sourceType: resolvedSourceType,
    sources: citation_data.sources
      ?.filter((s) => s.url)
      ?.map((s) => ({ url: s.url })),
    entityUrl: entity.url,
    entityName: entity.name,
    authorId: citation_data.author_id ?? undefined,
  };
}
